import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

const SoundPlayer = forwardRef(({ soundFile, mute, volume = 0.5 }, ref) => {
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const muteRef = useRef(mute);
  const volumeRef = useRef(volume);
  const lastPlayBySideRef = useRef({ "-1": 0, "1": 0 });
  muteRef.current = mute;
  volumeRef.current = volume;

  useEffect(() => {
    const context = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = context;

    let cancelled = false;
    fetch(soundFile)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch sound file: ${response.statusText}`);
        }
        return response.arrayBuffer();
      })
      .then((data) => context.decodeAudioData(data))
      .then((buffer) => {
        if (!cancelled) audioBufferRef.current = buffer;
      })
      .catch((error) => console.error("Audio loading error:", error));

    return () => {
      cancelled = true;
      audioBufferRef.current = null;
      audioContextRef.current = null;
      context.close().catch(() => {});
    };
  }, [soundFile]);

  useImperativeHandle(
    ref,
    () => ({
      play(pan) {
        const context = audioContextRef.current;
        const buffer = audioBufferRef.current;
        if (!context || !buffer) return;
        if (muteRef.current) return;

        // Per-side cooldown: swallow same-side bursts caused by
        // event-queue jitter (e.g., main-thread stalls bunching
        // animationiteration events together).
        const sideKey = pan < 0 ? "-1" : "1";
        const now = performance.now();
        if (now - lastPlayBySideRef.current[sideKey] < 200) return;
        lastPlayBySideRef.current[sideKey] = now;

        if (context.state === "suspended") {
          context.resume();
        }

        const source = context.createBufferSource();
        source.buffer = buffer;

        const gainNode = context.createGain();
        gainNode.gain.value = volumeRef.current;

        const panner = context.createStereoPanner();
        panner.pan.value = pan;

        source.connect(gainNode).connect(panner).connect(context.destination);
        source.start(0);
      },
    }),
    [],
  );

  return null;
});

export default SoundPlayer;
